import * as React from 'react';
import {
  LocationPalette,
  ColorDesignConfig,
  LayoutDesignConfig,
  FontDesignConfig,
  ThemeDesignConfig,
  DesignSectionConfig
} from './types';

import { DesignDemo_designShowcase_organization_allStyles } from '@graphql/designShowcaseTypes';
import { INITIAL_STATE, hydrateStateFromSearch } from './util/hydrateStateFromSearch';
import { api } from '../DesignShowcase/modules/api';
import { ApolloError } from 'apollo-client';
import { buildReactStateHandler } from '@lib';
import { ErrorPage } from '@components/Error';
import { DesignDemoState } from '../DesignDemo';

const stateUpdater = buildReactStateHandler<PaletteHydrationState>();

export type PaletteHydrationState = Readonly<{
  palette: LocationPalette;
  sections: {
    themes: ThemeDesignConfig;
    layout: LayoutDesignConfig;
    colors: ColorDesignConfig;
    fonts: FontDesignConfig;
    advanced: DesignSectionConfig;
  };
  error: null | ApolloError;
}>;

interface WrapperProps {
  designDemoState?: DesignDemoState;
  shouldHydrateStateFromSearch: boolean;
  onHeaderClick: () => void;
  ctaText: {
    title: string;
    subtitle: string;
  };
  inPopup?: boolean;
  popupOpen?: boolean;
}

export type StyleByIdMap = {
  [styleKey: string]: DesignDemo_designShowcase_organization_allStyles;
};

export function withPaletteHydration(WrappedComponent: any) {
  return class extends React.Component<WrapperProps, PaletteHydrationState> {
    private styleByIdMap: StyleByIdMap;
    private styleData: DesignDemo_designShowcase_organization_allStyles[];

    readonly state: PaletteHydrationState = {
      ...INITIAL_STATE
    };

    componentDidMount() {
      if (this.props.shouldHydrateStateFromSearch) {
        this.requestThemeData();
      }
    }

    requestThemeData = async () => {
      try {
        const response = await api.requestDemoThemes();
        if (response && response.designShowcase) {
          this.initializeStyleData(response.designShowcase.organization.allStyles);
          this.setState(hydrateStateFromSearch(true, this.styleByIdMap));
        }
      } catch (e) {
        this.setState(
          stateUpdater(draft => {
            draft.error = e;
          })
        );
      }
    };

    initializeStyleData = (styleData: DesignDemo_designShowcase_organization_allStyles[]) => {
      this.styleData = styleData;
      const styleByIdMap: StyleByIdMap = {};
      this.styleData.forEach(style => {
        styleByIdMap[style.id] = style;
      });
      this.styleByIdMap = styleByIdMap;
    };

    render() {
      const { shouldHydrateStateFromSearch, designDemoState, ctaText } = this.props;
      if (this.state.error) {
        return (
          <ErrorPage
            error={this.state.error}
            page="preview"
            pagePrefix="marketing"
            category="CreateWedding"
            attemptedAction="Request Demo Themes"
          />
        );
      }
      return (
        <WrappedComponent
          ctaText={ctaText}
          data={shouldHydrateStateFromSearch ? this.state : designDemoState}
          {...this.props}
        />
      );
    }
  };
}
